import { useMemo, useState } from "react"

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTranslation } from "react-i18next"

import type { LibraryEditor } from "./use-library-editor"

import { Columns3, GripVertical, Plus, Sparkles, Tags, X } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import { cn } from "@/lib/utils"

import { CsvInput, EditorSectionCard } from "./editor-fields"
import { ExtraDataColumnPicker } from "./extra-data-column-picker"
import {
  addInspectedColumns,
  EXTRA_DATA_RENDERS,
  EXTRA_DATA_TYPES,
  type ExtraDataRender,
  type ExtraDataRow,
  type ExtraDataRowErrors,
  type ExtraDataSuggestion,
  isKnownExtraDataType,
  isObjectExtraDataType,
  mergeSuggestedRows,
  missingSuggestedRows,
  newExtraDataRow,
  suggestedExtraDataFor,
  withExtraDataType,
} from "./extra-data-model"
import { isPagesType } from "./library-editor-model"

type ExtraDataSectionProps = {
  editor: LibraryEditor
}

/**
 * `extra_data_definition` as a list of rows: type, path, display name, the three flags and the
 * object keys where the type needs them. Rows can be added one by one, from a suggested set per
 * source type, removed, and reordered by drag (or keyboard on the handle).
 */
export function ExtraDataSection({ editor }: ExtraDataSectionProps) {
  const { t } = useTranslation()
  const rows = editor.extraDataRows
  const rowErrors = editor.extraDataErrors
  const sourceType = editor.values.source_type
  const suggestions = useMemo(() => suggestedExtraDataFor(sourceType), [sourceType])
  // Pages are read from the Graph sitePage object, not from list columns: the picker would offer
  // rows that stay empty at index time. The standard set already uses page properties.
  const columnsFromList = !isPagesType(sourceType)
  const [pickerOpen, setPickerOpen] = useState(false)
  const inspecting = editor.inspect.status === "loading"
  const inspected = editor.inspect.result

  const openColumnPicker = async () => {
    if (inspected) {
      setPickerOpen(true)
      return
    }
    const result = await editor.runInspect()
    if (result) setPickerOpen(true)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const updateRow = (key: string, patch: Partial<ExtraDataRow>) =>
    editor.updateExtraDataRows(rows.map((row) => (row.key === key ? { ...row, ...patch } : row)))
  const removeRow = (key: string) =>
    editor.updateExtraDataRows(rows.filter((row) => row.key !== key))
  const addRow = () => editor.updateExtraDataRows([...rows, newExtraDataRow()])
  const addSuggestion = (suggestion: ExtraDataSuggestion) =>
    editor.updateExtraDataRows(mergeSuggestedRows(rows, suggestion))

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const from = rows.findIndex((row) => row.key === active.id)
    const to = rows.findIndex((row) => row.key === over.id)
    if (from < 0 || to < 0) return
    editor.updateExtraDataRows(arrayMove(rows, from, to))
  }

  const suggestionLabel = (id: ExtraDataSuggestion["id"]) =>
    id === "attachments"
      ? t(
          "adminDashboard.libraries.editor.extraData.suggestion.attachments",
          "Add attachment links"
        )
      : t("adminDashboard.libraries.editor.extraData.suggestion.standard", "Add standard fields")

  return (
    <EditorSectionCard
      badge={rows.length > 0 ? <Badge variant="secondary">{rows.length}</Badge> : undefined}
      icon={<Tags className="size-4" />}
      id="library-extra-data"
      open={editor.openSections.extraData}
      title={t("adminDashboard.libraries.editor.sections.extraData", "Extra data")}
      onOpenChange={(open) => editor.setSectionOpen("extraData", open)}
    >
      <p className="text-muted-foreground text-xs">
        {t(
          "adminDashboard.libraries.editor.extraData.help",
          "Per-document fields read from SharePoint and stored with every chunk. They show in the citation details and, when chosen, are given to the model as metadata. Changes apply to documents indexed after the change; re-index the library to apply them to what is already indexed."
        )}
      </p>
      {editor.errors.extra_data_definition && (
        <p className="text-destructive text-xs">{editor.errors.extra_data_definition}</p>
      )}

      <div className="flex flex-wrap gap-2">
        {columnsFromList && (
          <Button
            disabled={!editor.canInspect || inspecting}
            size="sm"
            type="button"
            variant="outline"
            onClick={() => void openColumnPicker()}
          >
            {inspecting ? <Spinner /> : <Columns3 className="size-3.5" />}
            {t(
              "adminDashboard.libraries.editor.extraData.columns.button",
              "Add columns from SharePoint"
            )}
          </Button>
        )}
        {suggestions.map((suggestion) => {
          const missing = missingSuggestedRows(rows, suggestion).length
          const partial = missing > 0 && missing < suggestion.rows.length
          return (
            <Button
              key={suggestion.id}
              disabled={missing === 0}
              size="sm"
              type="button"
              variant="outline"
              title={
                missing === 0
                  ? t(
                      "adminDashboard.libraries.editor.extraData.suggestionAdded",
                      "All of these fields are already present"
                    )
                  : undefined
              }
              onClick={() => addSuggestion(suggestion)}
            >
              <Sparkles className="size-3.5" />
              {suggestionLabel(suggestion.id)}
              {partial ? ` (+${missing})` : ""}
            </Button>
          )
        })}
      </div>
      {columnsFromList && !editor.canInspect && (
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.libraries.editor.extraData.columns.needsAuth",
            "Columns can be read once the hostname, site and credentials are in place."
          )}
        </p>
      )}
      {editor.inspect.status === "error" && editor.inspect.error && (
        <p className="text-destructive text-xs">{editor.inspect.error}</p>
      )}
      {pickerOpen && inspected && (
        <ExtraDataColumnPicker
          columns={inspected.columns}
          listLabel={inspected.list_display_name || inspected.list_name}
          rows={rows}
          onClose={() => setPickerOpen(false)}
          onAdd={(columns) => {
            editor.updateExtraDataRows(addInspectedColumns(rows, columns))
            setPickerOpen(false)
          }}
        />
      )}

      {rows.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-3 text-center text-xs">
          {t(
            "adminDashboard.libraries.editor.extraData.empty",
            "No extra fields yet. Add the standard set or a single field."
          )}
        </p>
      ) : (
        <DndContext collisionDetection={closestCenter} sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={rows.map((row) => row.key)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {rows.map((row) => (
                <ExtraDataRowCard
                  key={row.key}
                  errors={rowErrors.get(row.key)}
                  row={row}
                  onChange={(patch) => updateRow(row.key, patch)}
                  onRemove={() => removeRow(row.key)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Button size="sm" type="button" variant="outline" onClick={addRow}>
        <Plus className="size-4" />
        {t("adminDashboard.libraries.editor.extraData.addField", "Add field")}
      </Button>
    </EditorSectionCard>
  )
}

type ExtraDataRowCardProps = {
  row: ExtraDataRow
  errors?: ExtraDataRowErrors
  onChange: (patch: Partial<ExtraDataRow>) => void
  onRemove: () => void
}

function ExtraDataRowCard({ row, errors, onChange, onRemove }: ExtraDataRowCardProps) {
  const { t } = useTranslation()
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.key })
  const style = { transform: CSS.Transform.toString(transform), transition }
  const fieldId = (suffix: string) => `${row.key}-${suffix}`
  const isObject = isObjectExtraDataType(row.type)
  const showKeys = isObject || row.select_values.length > 0
  const unknownType = row.type !== "" && !isKnownExtraDataType(row.type)

  const renderLabels: Record<ExtraDataRender, string> = {
    text: t("adminDashboard.libraries.editor.extraData.renders.text", "Text"),
    html: t("adminDashboard.libraries.editor.extraData.renders.html", "Formatted text"),
    link: t("adminDashboard.libraries.editor.extraData.renders.link", "Link"),
  }

  const typeLabels: Record<string, string> = {
    field: t("adminDashboard.libraries.editor.extraData.types.field", "Field"),
    "field-lookup": t(
      "adminDashboard.libraries.editor.extraData.types.fieldLookup",
      "Lookup field"
    ),
    list: t("adminDashboard.libraries.editor.extraData.types.list", "List"),
    list_objects: t(
      "adminDashboard.libraries.editor.extraData.types.listObjects",
      "List of objects"
    ),
    list_objects_concat: t(
      "adminDashboard.libraries.editor.extraData.types.listObjectsConcat",
      "JSON object"
    ),
  }
  const typeHelp: Record<string, string> = {
    field: t("adminDashboard.libraries.editor.extraData.typeHelp.field", "One value at the path."),
    "field-lookup": t(
      "adminDashboard.libraries.editor.extraData.typeHelp.fieldLookup",
      "A lookup column, e.g. field_7LookupId."
    ),
    list: t(
      "adminDashboard.libraries.editor.extraData.typeHelp.list",
      "An array at the path; every entry is kept."
    ),
    list_objects: t(
      "adminDashboard.libraries.editor.extraData.typeHelp.listObjects",
      "An array of objects; the keys below are read from each."
    ),
    list_objects_concat: t(
      "adminDashboard.libraries.editor.extraData.typeHelp.listObjectsConcat",
      "A JSON string holding one object; the keys below are read and joined."
    ),
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-card space-y-2 rounded-md border p-2",
        isDragging && "opacity-70 shadow-md"
      )}
    >
      <div className="flex items-start gap-2">
        <button
          ref={setActivatorNodeRef}
          aria-label={t("adminDashboard.libraries.editor.extraData.move", "Drag to reorder")}
          className="text-muted-foreground hover:text-foreground mt-7 cursor-grab touch-none"
          type="button"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="size-4" />
        </button>
        <div className="grid flex-1 grid-cols-1 gap-2 sm:grid-cols-[minmax(0,11rem)_1fr_1fr]">
          <div className="space-y-1">
            <Label className="text-xs" htmlFor={fieldId("type")}>
              {t("adminDashboard.libraries.editor.extraData.type", "Type")}
            </Label>
            <Select
              value={row.type}
              onValueChange={(type) => onChange(withExtraDataType(row, type))}
            >
              <SelectTrigger
                className={cn("h-8 text-xs", errors?.type && "border-destructive")}
                id={fieldId("type")}
              >
                <SelectValue>{unknownType ? row.type : undefined}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {EXTRA_DATA_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {typeLabels[type]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors?.type && <p className="text-destructive text-xs">{errors.type}</p>}
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor={fieldId("path")}>
              {t("adminDashboard.libraries.editor.extraData.path", "Path")}
            </Label>
            <Input
              className={cn("h-8 font-mono text-xs", errors?.path && "border-destructive")}
              id={fieldId("path")}
              placeholder="webUrl"
              value={row.path}
              onChange={(event) => onChange({ path: event.target.value })}
            />
            {errors?.path && <p className="text-destructive text-xs">{errors.path}</p>}
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor={fieldId("name")}>
              {t("adminDashboard.libraries.editor.extraData.displayName", "Display name")}
            </Label>
            <Input
              className={cn("h-8 text-xs", errors?.display_name && "border-destructive")}
              id={fieldId("name")}
              placeholder="File URL"
              value={row.display_name}
              onChange={(event) => onChange({ display_name: event.target.value })}
            />
            {errors?.display_name && (
              <p className="text-destructive text-xs">{errors.display_name}</p>
            )}
          </div>
        </div>
        <Button
          aria-label={t("adminDashboard.libraries.editor.extraData.remove", "Remove field")}
          className="hover:text-destructive mt-6 size-7"
          size="icon"
          type="button"
          variant="ghost"
          onClick={onRemove}
        >
          <X className="size-4" />
        </Button>
      </div>

      {typeHelp[row.type] && (
        <p className="text-muted-foreground pl-6 text-xs">{typeHelp[row.type]}</p>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pl-6">
        <FlagCheckbox
          checked={row.add_to_prompt}
          id={fieldId("prompt")}
          label={t("adminDashboard.libraries.editor.extraData.addToPrompt", "Give to the model")}
          onChange={(add_to_prompt) => onChange({ add_to_prompt })}
        />
        <FlagCheckbox
          checked={row.add_to_ui}
          id={fieldId("ui")}
          label={t("adminDashboard.libraries.editor.extraData.addToUi", "Show in citation details")}
          onChange={(add_to_ui) => onChange({ add_to_ui })}
        />
        <div className="flex items-center gap-2">
          <Label className="text-xs" htmlFor={fieldId("render")}>
            {t("adminDashboard.libraries.editor.extraData.render", "Show as")}
          </Label>
          <Select
            value={row.render}
            onValueChange={(render) =>
              // `links` is kept in step so anything still reading the old flag agrees with the
              // dropdown -- the two can never disagree (#3008).
              onChange({ render: render as ExtraDataRender, links: render === "link" })
            }
          >
            <SelectTrigger className="h-8 w-32 text-xs" id={fieldId("render")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EXTRA_DATA_RENDERS.map((render) => (
                <SelectItem key={render} value={render}>
                  {renderLabels[render]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {showKeys && (
        <div className="space-y-1 pl-6">
          <Label className="text-xs" htmlFor={fieldId("select")}>
            {t("adminDashboard.libraries.editor.extraData.selectValues", "Keys to read")}
          </Label>
          <CsvInput
            id={fieldId("select")}
            placeholder="webUrl, name"
            values={row.select_values}
            onChange={(select_values) => onChange({ select_values })}
          />
          <p className="text-muted-foreground text-xs">
            {t(
              "adminDashboard.libraries.editor.extraData.selectValuesHelp",
              "Comma-separated keys picked from each object."
            )}
          </p>
          {errors?.select_values && (
            <p className="text-destructive text-xs">{errors.select_values}</p>
          )}
        </div>
      )}
    </div>
  )
}

type FlagCheckboxProps = {
  id: string
  checked: boolean
  label: string
  onChange: (checked: boolean) => void
}

function FlagCheckbox({ id, checked, label, onChange }: FlagCheckboxProps) {
  return (
    <div className="flex items-center gap-2">
      <Checkbox checked={checked} id={id} onCheckedChange={(value) => onChange(value === true)} />
      <Label className="text-xs font-normal" htmlFor={id}>
        {label}
      </Label>
    </div>
  )
}
